import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Sidebar from './sidebar';
import OrderQuickView from './modal_windows/OrderQuickView';

import {
  StatCard, RefreshButton, SearchField, FilterPill,
  StatusBadge, TableCard, LoadingState, EmptyState, Pagination,
} from '../../ui/CrmPage';

const PAGE_SIZE = 15;

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const Logistics = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCity, setSelectedCity] = useState('Все');
  const [currentPage, setCurrentPage] = useState(1);
  const [detailRow, setDetailRow] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}v1/logistics_view`, {
          validateStatus: (s) => s >= 200 && s < 500,
        });
        const list = Array.isArray(response.data) ? response.data : [];
        const sorted = [...list].sort((a, b) => {
          const ap = String(a.production_day || '');
          const bp = String(b.production_day || '');
          if (ap === bp) return String(a.id_drivers || '').localeCompare(String(b.id_drivers || ''));
          return ap.localeCompare(bp);
        });
        if (!cancelled) setOrders(sorted);
      } catch {
        if (!cancelled) setOrders([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  const sendInTransit = async (order) => {
    await axios.put(`${process.env.REACT_APP_API_URL}v1/orders/${order.id}`, {
      ...order, id_status: String(5),
    });
    setOrders(prev => prev.map(o => o.id === order.id ? { ...o, id_status: String(5), status_name: 'В пути' } : o));
  };
  const confirmDelivered = async (order) => {
    await axios.put(`${process.env.REACT_APP_API_URL}v1/orders/${order.id}`, {
      ...order, id_status: String(2),
    });
    setOrders(prev => prev.map(o => o.id === order.id ? { ...o, id_status: String(2), status_name: 'Доставлен' } : o));
  };

  const cities = useMemo(() =>
    Array.from(new Set(orders.map(o => o.region_name).filter(Boolean))), [orders]);

  const summary = useMemo(() => ({
    total: orders.length,
    ready: orders.filter(o => /готов/i.test(o.status_name || '')).length,
    inTransit: orders.filter(o => /в пути|доставк/i.test(o.status_name || '')).length,
    delivered: orders.filter(o => /доставлен/i.test(o.status_name || '')).length,
  }), [orders]);

  const filteredOrders = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return orders.filter(o => {
      if (selectedCity !== 'Все' && o.region_name !== selectedCity) return false;
      if (!term) return true;
      return (
        String(o.order_number || '').includes(term) ||
        (o.vehicle_name || '').toLowerCase().includes(term) ||
        (o.region_name || '').toLowerCase().includes(term) ||
        (o.status_name || '').toLowerCase().includes(term) ||
        (o.client_name || '').toLowerCase().includes(term) ||
        (o.delivery_address || '').toLowerCase().includes(term)
      );
    });
  }, [orders, searchTerm, selectedCity]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const paginatedOrders = filteredOrders.slice(pageStart, pageStart + PAGE_SIZE);

  const resetFilters = () => { setSearchTerm(''); setSelectedCity('Все'); setCurrentPage(1); };
  const hasFilters = searchTerm || selectedCity !== 'Все';

  return (
    <div className="container-logistics crmpage">
      <div className="sidebar-container-logistics">
        <Sidebar />
      </div>

      <div className="content-logistics crmpage__main">
        <header className="crmpage__head">
          <h1 className="crmpage__title">Логистика</h1>
        </header>

        <section className="crmpage__stats">
          <StatCard label="Всего"      value={summary.total}     accent="neutral" />
          <StatCard label="Готовы"     value={summary.ready}     accent="success" />
          <StatCard label="В пути"     value={summary.inTransit} accent="info" />
          <StatCard label="Доставлены" value={summary.delivered} accent="warning" />
          <RefreshButton onClick={() => setReloadKey(k => k + 1)} />
        </section>

        <section className="crmpage__filters">
          <SearchField
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            placeholder="Поиск: номер, водитель, регион…"
          />
          <div className="crmpage__pills">
            <FilterPill active={selectedCity === 'Все'}
              onClick={() => { setSelectedCity('Все'); setCurrentPage(1); }}
              count={orders.length}>Все города</FilterPill>
            {cities.map(c => (
              <FilterPill key={c}
                active={selectedCity === c}
                onClick={() => { setSelectedCity(c); setCurrentPage(1); }}
                count={orders.filter(o => o.region_name === c).length}>{c}</FilterPill>
            ))}
          </div>
          {hasFilters && (
            <button type="button" className="crmpage__reset" onClick={resetFilters}>Сбросить</button>
          )}
        </section>

        <TableCard>
          {loading ? (
            <LoadingState text="Загружаем логистику…" />
          ) : paginatedOrders.length === 0 ? (
            <EmptyState title="Нет заказов на отгрузке" text={hasFilters ? 'Сбросьте фильтры.' : 'Готовые заказы появятся здесь.'} />
          ) : (
            <>
              <div className="crmpage__table-wrap">
                <table className="crmpage__table">
                  <colgroup>
                    <col style={{ width: 80 }} />
                    <col />
                    <col style={{ width: 140 }} />
                    <col style={{ width: 160 }} />
                    <col style={{ width: 220 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Номер</th>
                      <th>Машина / Водитель</th>
                      <th className="crmpage__hide-880">Регион</th>
                      <th>Статус</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedOrders.map((order, index) => (
                      <tr key={order.id ?? index} className="crmpage__row" onClick={() => setDetailRow(order)}>
                        <td><span className="crmpage__num">№{order.order_number}</span></td>
                        <td>
                          <div className="crmpage__primary">{order.vehicle_name || '—'}</div>
                          <div className="crmpage__sub">{order.driver_name || ''}</div>
                        </td>
                        <td className="crmpage__muted crmpage__hide-880">{order.region_name || '—'}</td>
                        <td><StatusBadge name={order.status_name} /></td>
                        <td onClick={(e) => e.stopPropagation()}>
                          {order.status_name === 'Готов к отправке' && (
                            <button className="crmpage__btn crmpage__btn--primary" onClick={() => sendInTransit(order)}>В путь</button>
                          )}
                          {order.status_name === 'В пути' && (
                            <button className="crmpage__btn crmpage__btn--primary" onClick={() => confirmDelivered(order)}>Подтвердить</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="crmpage__cards">
                {paginatedOrders.map((o, idx) => (
                  <div key={idx} className="crmpage__card" onClick={() => setDetailRow(o)}>
                    <div className="crmpage__card-head">
                      <span className="crmpage__num">№{o.order_number}</span>
                      <StatusBadge name={o.status_name} />
                    </div>
                    <div className="crmpage__card-row">
                      <span className="crmpage__card-label">Машина</span>
                      <span className="crmpage__card-val">{o.vehicle_name || '—'}</span>
                    </div>
                    <div className="crmpage__card-row">
                      <span className="crmpage__card-label">Регион</span>
                      <span className="crmpage__card-val">{o.region_name || '—'}</span>
                    </div>
                  </div>
                ))}
              </div>

              <Pagination current={currentPage} total={totalPages} onChange={setCurrentPage} />
            </>
          )}
        </TableCard>

        <OrderQuickView
          isOpen={!!detailRow}
          row={detailRow}
          onClose={() => setDetailRow(null)}
        />
      </div>
    </div>
  );
};

export default Logistics;
